import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * IBAN length per country, from the ISO 13616 registry.
 * The length is fixed per country, so it doubles as the country-code whitelist.
 */
const IBAN_LENGTH_BY_COUNTRY: Record<string, number> = Object.fromEntries(
  (
    'AD24 AE23 AL28 AT20 AZ28 BA20 BE16 BG22 BH22 BI27 BR29 BY28 CH21 CR22 CY28 CZ24 DE22 DJ27 DK18 DO28 ' +
    'EE20 EG29 ES24 FI18 FK18 FO18 FR27 GB22 GE22 GI23 GL18 GR27 GT28 HN28 HR21 HU28 IE22 IL23 IQ23 IS26 ' +
    'IT27 JO30 KW30 KZ20 LB28 LC32 LI21 LT20 LU20 LV21 LY25 MC27 MD24 ME22 MK19 MN20 MR27 MT31 MU30 NI28 ' +
    'NL18 NO15 OM23 PK24 PL28 PS29 PT25 QA29 RO24 RS22 RU33 SA24 SC31 SD18 SE24 SI19 SK24 SM27 SO23 ST25 ' +
    'SV28 TL23 TN24 TR26 UA29 VA22 VG24 XK20 YE30'
  )
    .split(' ')
    .map((entry) => [entry.slice(0, 2), Number(entry.slice(2))]),
);

/**
 * Normalizes an IBAN for validation and storage: strips spaces/dashes and upper-cases it.
 * Banks and users routinely write IBANs grouped in fours ("DE89 3704 0044 ...").
 */
export function normalizeIban(iban: string): string {
  return (iban ?? '').replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Validates the *structure* of an IBAN per ISO 13616: known country code, correct length for that
 * country, and a valid ISO 7064 MOD-97-10 check digit.
 *
 * This catches every typo and transposition offline and is the only IBAN check that can be done on the
 * frontend. It deliberately does NOT prove the bank or account exists — that requires the backend
 * `validate-iban` lookup, which reports a different class of failure and must be surfaced separately.
 */
export function ibanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const iban = normalizeIban(control.value);

    // Let `Validators.required` own the empty case, as every other validator here does.
    if (!iban) return null;

    // The lookup already proves the first two characters are a known country code, so the remainder
    // only needs to be two check digits followed by an alphanumeric BBAN.
    const expectedLength = IBAN_LENGTH_BY_COUNTRY[iban.slice(0, 2)];
    if (!expectedLength || iban.length !== expectedLength || !/^[0-9]{2}[A-Z0-9]+$/.test(iban.slice(2))) {
      return { invalid: true };
    }

    // MOD-97-10: move the first four chars to the end, map letters to numbers (A=10 ... Z=35),
    // and check the remainder is 1. Reduce digit-by-digit to stay inside safe integer range.
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    const digits = rearranged.replace(/[A-Z]/g, (char) => String(char.charCodeAt(0) - 55));

    let remainder = 0;
    for (const digit of digits) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }

    return remainder === 1 ? null : { invalid: true };
  };
}

export function arrayNotEmptyValidator(): ValidatorFn {
  return (control: AbstractControl): Record<string, any> | null => {
    const isArray = Array.isArray(control.value);
    const isEmpty = isArray && control.value.length === 0;
    return isEmpty ? { arrayNotEmpty: { value: control.value } } : null;
  };
}

export function isValidDate(dateString: string): boolean {
  return /^\d{4}[-/]\d{2}[-/]\d{2}$/.test(dateString);
}

export function validateCustomDate(control: FormControl): ValidationErrors | null {
  if (!/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(control.value)) {
    return { invalidDateFormat: true };
  }
  return null;
}

// mustMatch validator function
export function mustMatch(controlName: string, matchingControlName: string): ValidatorFn {
  return (formGroup: FormGroup): ValidationErrors | null => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    if (matchingControl.errors && !matchingControl.errors['mustMatch']) {
      return null;
    }

    // set error on matchingControl if validation fails
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustMatch: true });
      return { mustMatch: true };
    } else {
      matchingControl.setErrors(null);
      return null;
    }
  };
}

/**
 * Creates a validator function to validate that one time is in the future relative to another.
 * @param startControlName The form control name for the start time.
 * @param endControlName The form control name for the end time.
 * @returns A validator function.
 */
export function timeRangeValidator(startControlName: string, endControlName: string) {
  return (group: FormGroup): ValidationErrors | null => {
    const startTime = group.get(startControlName)?.value;
    const endTime = group.get(endControlName)?.value;

    // Only perform time range validation if both times are provided.
    if (startTime && endTime) {
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);

      const startDate = new Date(2025, 0, 1, startHour, startMinute);
      const endDate = new Date(2025, 0, 1, endHour, endMinute);

      if (endDate <= startDate) {
        return { timeRangeInvalid: true };
      }
    }

    return null;
  };
}

/**
 * Creates a generic validator function to check if one date is after another within the same FormGroup.
 * @param startControlName The form control name for the start date.
 * @param endControlName The form control name for the end date.
 * @returns A validator function that adds an error if the end date is not after the start date.
 */
export function dateRangeValidator(startControlName: string, endControlName: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    if (!(group instanceof FormGroup)) return null;

    const startDate = group.get(startControlName)?.value;
    const endDate = group.get(endControlName)?.value;

    // Check if both dates are present before comparing
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Check if the end date is after the start date
      if (end < start) {
        // If not, return an error object on the form group
        const errorObject = { dateRangeInvalid: true };
        // Optionally, set errors directly on the end date control to make handling specific error messages easier
        group.get(endControlName)?.setErrors(errorObject);
        return errorObject;
      }
    }

    // If everything is fine, clear errors related to the date range from the end date control
    const endControlErrors = group.get(endControlName)?.errors;
    if (endControlErrors?.['dateRangeInvalid']) {
      delete endControlErrors['dateRangeInvalid'];
      group.get(endControlName)?.setErrors(Object.keys(endControlErrors).length ? endControlErrors : null);
    }

    // Return null if no validation errors
    return null;
  };
}

/**
 * Validates that one date control's value is less than another, where one control is within a FormArray.
 * @param form The FormGroup containing both the FormArray and the control to compare.
 * @param arrayPath The path to the FormArray in the FormGroup.
 * @param arrayDateControlName The name of the control within the FormArray groups to compare.
 * @param controlName The name of the control outside the FormArray to compare against the array's date control.
 * @returns A validator function that applies the specified validation.
 */
export function dateRangeArrayAndControlValidator(form: FormGroup, arrayPath: string, arrayDateControlName: string, controlName: string): ValidatorFn {
  return (control: AbstractControl): Record<string, any> | null => {
    const dateArray = form?.get(arrayPath) as FormArray;
    const endDateControl = control.get(controlName);
    const endDate = endDateControl?.value;

    if (dateArray && dateArray.length > 0 && endDate) {
      const firstEventGroup = dateArray.at(0) as FormGroup;
      const startDate = firstEventGroup.get(arrayDateControlName)?.value;

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Set true if invalid, false otherwise
        const isInvalid = end <= start;
        endDateControl?.setErrors(isInvalid ? { dateRangeInvalid: true } : null);
        return isInvalid ? { dateRangeInvalid: true } : null;
      }
    }

    // If the conditions don't hold or are valid, clear any errors related to date range
    endDateControl?.setErrors(null);
    return null; // If all checks pass, return null
  };
}

/**
 * Creates a validator function to ensure the uniqueness of a field within a FormArray or FormGroup.
 * @param parentControl A reference to the parent FormArray or FormGroup.
 * @param fieldName The name of the field within the form groups to check for uniqueness.
 * @returns A validator function.
 */
export function uniqueFieldValidator(parentControl: AbstractControl, fieldName: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const currentValue = control.value;
    let occurrences = 0;

    // Handling for FormArray
    if (parentControl instanceof FormArray) {
      occurrences = parentControl.controls.filter((ctrl) => ctrl.get(fieldName)?.value === currentValue).length;
    }
    // Handling for FormGroup
    else if (parentControl instanceof FormGroup) {
      const controls = Object.values(parentControl.controls);
      occurrences = controls.filter((ctrl) => {
        // Assuming each control is a FormGroup that might contain the fieldName
        if (ctrl instanceof FormGroup) {
          return ctrl.get(fieldName)?.value === currentValue;
        }
        return false;
      }).length;
    }

    return occurrences > 1 ? { uniqueField: true } : null;
  };
}
